/**
 * Turns the product-card "Add to cart" button into a live quantity stepper.
 *
 * First click adds the item; after that the button is replaced by a - 1 + control
 * bound to that variant's line in the cart. Dropping to zero removes the line and
 * restores the button.
 *
 * Deliberately talks to Shopify's AJAX cart directly rather than reusing the
 * theme's <product-form-component>: the stepper needs the resulting cart object
 * back so it can re-sync every card on the page, and product-form-component does
 * not hand that over. The theme's own header bubble and cart drawer are kept in
 * sync by re-emitting Horizon's `shopify:cart:lines-update` event.
 */
const CART_EVENT = 'shopify:cart:lines-update';
const SOURCE = 'toyverse-cart-qty';

/** @param {number} count */
function emitCartUpdate(cart) {
  const count = cart?.item_count ?? 0;
  // `detail` is read-only on CustomEvent, so it has to go through the
  // constructor; `action` and `promise` are our own props and can be assigned.
  const event = new CustomEvent(CART_EVENT, {
    bubbles: true,
    cancelable: false,
    detail: { itemCount: count, source: SOURCE },
  });
  // Horizon's cart-icon / cart-drawer read `action` and `promise` off the event.
  // 'update' rather than 'add' so a drawer with auto-open does not fly open on
  // every tap of the plus button.
  event.action = 'update';
  event.promise = Promise.resolve({
    cart: { totalQuantity: count },
    detail: { itemCount: count, source: SOURCE },
  });
  document.dispatchEvent(event);
}

class ToyverseCartQty extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.variantId = String(this.dataset.variantId || '');
    this.addBtn = this.querySelector('[data-tv-add]');
    this.stepper = this.querySelector('[data-tv-stepper]');
    this.countEl = this.querySelector('[data-tv-count]');

    this.addBtn?.addEventListener('click', () => this.#change(1));
    this.querySelector('[data-tv-plus]')?.addEventListener('click', () => this.#change(1));
    this.querySelector('[data-tv-minus]')?.addEventListener('click', () => this.#change(-1));

    document.addEventListener(CART_EVENT, this.#onExternalCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_EVENT, this.#onExternalCartUpdate);
  }

  /** Another component changed the cart (PDP form, cart drawer) — re-read the truth. */
  #onExternalCartUpdate = (event) => {
    if (event.detail?.source === SOURCE) return;
    ToyverseCartQty.resyncAll();
  };

  get quantity() {
    return Number(this.dataset.quantity || 0);
  }

  set quantity(value) {
    const qty = Math.max(0, Number(value) || 0);
    this.dataset.quantity = String(qty);
    if (this.countEl) this.countEl.textContent = String(qty);
    this.addBtn?.toggleAttribute('hidden', qty > 0);
    this.stepper?.toggleAttribute('hidden', qty === 0);
  }

  #setBusy(state) {
    this.#busy = state;
    this.toggleAttribute('data-busy', state);
    for (const button of this.querySelectorAll('button')) button.disabled = state;
  }

  async #change(delta) {
    if (this.#busy || !this.variantId) return;
    const current = this.quantity;
    const next = Math.max(0, current + delta);

    this.#setBusy(true);
    // Optimistic: the control responds on the tap, not on the round trip.
    this.quantity = next;

    try {
      const cart =
        current === 0
          ? await this.#post('/cart/add.js', { items: [{ id: this.variantId, quantity: 1 }] })
          : await this.#post('/cart/change.js', { id: this.variantId, quantity: next });

      // /cart/add.js returns the added lines, not the cart, so read the cart back.
      const state = cart?.item_count === undefined ? await (await fetch('/cart.js')).json() : cart;
      ToyverseCartQty.applyCart(state);
      emitCartUpdate(state);
    } catch (error) {
      console.warn('[toyverse-cart-qty] cart update failed', error);
      this.quantity = current;
    } finally {
      this.#setBusy(false);
    }
  }

  async #post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`${url} -> ${response.status}`);
    return response.json();
  }

  /** Point every stepper on the page at the quantities in this cart object. */
  static applyCart(cart) {
    const byVariant = new Map();
    for (const line of cart?.items ?? []) byVariant.set(String(line.variant_id), line.quantity);
    for (const el of document.querySelectorAll('tv-cart-qty')) {
      el.quantity = byVariant.get(String(el.dataset.variantId)) ?? 0;
    }
  }

  static async resyncAll() {
    try {
      ToyverseCartQty.applyCart(await (await fetch('/cart.js')).json());
    } catch (error) {
      console.warn('[toyverse-cart-qty] resync failed', error);
    }
  }
}

if (!customElements.get('tv-cart-qty')) {
  customElements.define('tv-cart-qty', ToyverseCartQty);
}

// Cards can be rendered from a cached page, so trust /cart.js over the markup.
document.addEventListener('DOMContentLoaded', () => ToyverseCartQty.resyncAll());
window.addEventListener('pageshow', (event) => {
  if (event.persisted) ToyverseCartQty.resyncAll();
});
